// Clipboard utilities for copying table data in Excel-compatible format

/**
 * Copy column data to clipboard in Excel-compatible format
 * @param {Array} data - Array of data objects
 * @param {string} columnKey - The key of the column to copy
 * @param {string} columnName - Display name of the column
 * @param {boolean} includeHeader - Whether to include column header
 * @returns {Promise<Object>} Result of the copy operation
 */
export const copyColumnToClipboard = async (
  data,
  columnKey,
  columnName = "Column",
  includeHeader = true
) => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error("No data available to copy");
    }

    // Extract column values preserving the exact row structure (including empty rows for section headers)
    const columnValues = data.map((row) => {
      // If this is a section header, return empty string to preserve structure
      if (row.is_section_header) {
        return "";
      }

      let value;

      // Handle different column accessor patterns
      if (columnKey === "room_allocation") {
        // Special handling for room allocation column (primary field or fallback)
        value = row.room_allocation || row.room_auto_allo || "";
      } else if (columnKey.includes(".")) {
        // Handle nested properties (e.g., "user.name")
        const keys = columnKey.split(".");
        value = keys.reduce((obj, key) => obj?.[key], row);
      } else {
        // Standard property access
        value = row[columnKey];
      }

      // Convert to string and handle null/undefined values
      if (value === null || value === undefined) {
        return "";
      }

      // Handle numeric values
      if (typeof value === "number") {
        return value.toString();
      }

      // Handle objects (stringify them)
      if (typeof value === "object") {
        return JSON.stringify(value);
      }

      // Convert to string and clean up
      return String(value).trim();
    });

    // Build the clipboard content
    let clipboardContent = "";

    if (includeHeader) {
      clipboardContent += columnName + "\n";
    }

    // Add all values, one per line (Excel-compatible format)
    clipboardContent += columnValues.join("\n");

    // Copy to clipboard using the modern Clipboard API
    await navigator.clipboard.writeText(clipboardContent);

    // Count only non-empty values for the message
    const nonEmptyCount = columnValues.filter((val) => val !== "").length;

    // Show success notification
    console.log(
      `✅ Copied ${nonEmptyCount} ${columnName} values to clipboard (${columnValues.length} total rows including section headers)`
    );

    return {
      success: true,
      message: `Copied ${nonEmptyCount} ${columnName} values to clipboard`,
      copiedCount: nonEmptyCount,
      totalRows: columnValues.length,
      previewData: columnValues.filter((val) => val !== "").slice(0, 5), // First 5 non-empty values for preview
    };
  } catch (error) {
    console.error("❌ Error copying column to clipboard:", error);

    // Fallback to older method if Clipboard API fails
    if (error.name === "NotAllowedError" || !navigator.clipboard) {
      try {
        return await fallbackCopyToClipboard(data, columnKey, columnName, includeHeader);
      } catch (fallbackError) {
        console.error("❌ Fallback copy method also failed:", fallbackError);
        return {
          success: false,
          message: `Failed to copy: ${fallbackError.message}`,
        };
      }
    }

    return {
      success: false,
      message: `Failed to copy: ${error.message}`,
    };
  }
};

/**
 * Fallback method for copying to clipboard using document.execCommand
 * @param {Array} data - Array of data objects
 * @param {string} columnKey - The key of the column to copy
 * @param {string} columnName - Display name of the column
 * @param {boolean} includeHeader - Whether to include column header
 * @returns {Promise<Object>} Result of the copy operation
 */
const fallbackCopyToClipboard = async (data, columnKey, columnName, includeHeader) => {
  return new Promise((resolve, reject) => {
    try {
      // Create temporary textarea element
      const textarea = document.createElement("textarea");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      textarea.style.opacity = "0";

      // Extract column values preserving the exact row structure (including empty rows for section headers)
      const columnValues = data.map((row) => {
        // If this is a section header, return empty string to preserve structure
        if (row.is_section_header) {
          return "";
        }

        let value;
        if (columnKey === "room_allocation") {
          value = row.room_allocation || row.room_auto_allo || "";
        } else {
          value = row[columnKey];
        }

        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value).trim();
      });

      // Build content
      let content = "";
      if (includeHeader) {
        content += columnName + "\n";
      }
      content += columnValues.join("\n");

      textarea.value = content;
      document.body.appendChild(textarea);

      // Select and copy
      textarea.select();
      textarea.setSelectionRange(0, 99999); // For mobile devices

      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (successful) {
        // Count only non-empty values for the message
        const nonEmptyCount = columnValues.filter((val) => val !== "").length;
        console.log(
          `✅ Fallback copy successful: ${nonEmptyCount} values (${columnValues.length} total rows including section headers)`
        );
        resolve({
          success: true,
          message: `Copied ${nonEmptyCount} ${columnName} values to clipboard`,
          copiedCount: nonEmptyCount,
          totalRows: columnValues.length,
          previewData: columnValues.filter((val) => val !== "").slice(0, 5),
        });
      } else {
        reject(new Error("Fallback copy command failed"));
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Copy entire table data to clipboard in Excel-compatible format
 * @param {Array} data - Array of data objects
 * @param {Array} columns - Array of column definitions
 * @param {string} tableName - Name of the table for the copy operation
 * @returns {Promise<Object>} Result of the copy operation
 */
export const copyTableToClipboard = async (data, columns, tableName = "Table") => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error("No data available to copy");
    }

    // Filter out section headers
    const validRows = data.filter((row) => !row.is_section_header);

    // Build header row
    const headers = columns.map((col) => col.Header || col.accessor).join("\t");

    // Build data rows
    const dataRows = validRows.map((row) => {
      return columns
        .map((col) => {
          let value;

          if (typeof col.accessor === "function") {
            value = col.accessor(row);
          } else {
            value = row[col.accessor];
          }

          if (value === null || value === undefined) return "";
          if (typeof value === "object") return JSON.stringify(value);
          return String(value).trim();
        })
        .join("\t");
    });

    // Combine headers and data with tab separation (Excel format)
    const clipboardContent = [headers, ...dataRows].join("\n");

    // Copy to clipboard
    await navigator.clipboard.writeText(clipboardContent);

    console.log(`✅ Copied entire ${tableName} table to clipboard`);

    return {
      success: true,
      message: `Copied entire ${tableName} table (${validRows.length} rows) to clipboard`,
      copiedCount: validRows.length,
      columnsCount: columns.length,
    };
  } catch (error) {
    console.error("❌ Error copying table to clipboard:", error);
    return {
      success: false,
      message: `Failed to copy table: ${error.message}`,
    };
  }
};

/**
 * Show a visual feedback notification for copy operations
 * @param {Object} result - Result object from copy operation
 * @param {string} fallbackMessage - Message to show if no result message
 */
export const showCopyNotification = (result, fallbackMessage = "Copied to clipboard") => {
  if (result.success) {
    // Show success notification with preview if available
    let message = result.message;
    if (result.previewData && result.previewData.length > 0) {
      const preview = result.previewData.slice(0, 3).join(", ");
      message += `\nPreview: ${preview}${result.previewData.length > 3 ? "..." : ""}`;
    }

    // You can integrate with a toast library here
    console.log("📋 " + message);

    // Simple browser notification (optional)
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Copied to Clipboard", {
        body: result.message,
        icon: "/favicon.ico",
        silent: true,
      });
    }
  } else {
    console.error("❌ Copy failed: " + result.message);
    alert("Copy failed: " + result.message);
  }
};

/**
 * Request notification permission (call this on app initialization)
 */
export const requestNotificationPermission = () => {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission();
  }
};
